import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { useLang, CONTACT } from "../i18n";

const HERO_VIDEO = "https://videos.pexels.com/video-files/855271/855271-hd_1920_1080_25fps.mp4";
const HERO_POSTER =
  "https://images.unsplash.com/photo-1609867271967-a82f85c48531?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";

const waHref = (label) => `https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent(label)}`;

const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 0.9, delay: 0.6 + i * 0.15, ease: [0.22, 1, 0.36, 1] } }),
};

export default function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yVideo = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const yText = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="top" ref={ref} className="relative h-[100svh] min-h-[640px] w-full overflow-hidden grain">
      {/* Video / parallax layer */}
      <motion.div style={{ y: yVideo }} className="absolute inset-0 -z-0 scale-110">
        <video
          className="h-full w-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          poster={HERO_POSTER}
          data-testid="hero-video"
        >
          <source src={HERO_VIDEO} type="video/mp4" />
        </video>
      </motion.div>

      {/* Scrims */}
      <div className="absolute inset-0 bg-black/55" />
      <div className="absolute inset-0 bg-gradient-to-t from-bg via-black/20 to-black/40" />

      {/* Content */}
      <motion.div
        style={{ y: yText, opacity }}
        className="relative z-10 h-full mx-auto max-w-7xl px-5 md:px-8 flex flex-col justify-end pb-20 md:pb-24"
      >
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="flex items-center gap-3 mb-6"
        >
          <span className="h-px w-10 bg-brand-orange" />
          <span className="text-xs md:text-sm font-bold tracking-[0.25em] uppercase text-brand-orange">
            {t.hero.overline}
          </span>
        </motion.div>

        <h1 className="font-display font-semibold tracking-tighter leading-[0.92] text-shadow-strong text-[13vw] sm:text-6xl md:text-7xl lg:text-[6.2rem] max-w-5xl">
          {[t.hero.line1, t.hero.line2].map((line, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="show"
                className={`inline-block ${i === 1 ? "text-brand-orange" : ""}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.7 }}
          className="mt-7 max-w-xl text-base md:text-lg text-steel-300 leading-relaxed"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4, duration: 0.7 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <a
            href={waHref(t.hero.cta)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-cta"
            className="group inline-flex items-center gap-2 bg-brand-orange text-black font-semibold px-7 py-4 rounded-full hover:bg-white transition-colors duration-300"
          >
            {t.hero.cta}
          </a>
          <button
            onClick={() => document.getElementById("galeria")?.scrollIntoView({ behavior: "smooth" })}
            data-testid="hero-secondary"
            className="inline-flex items-center gap-2 border border-white/25 text-white font-medium px-7 py-4 rounded-full hover:border-white hover:bg-white/5 transition-colors duration-300"
          >
            {t.hero.secondary}
          </button>
        </motion.div>
      </motion.div>

      {/* Scroll cue */}
      <motion.div
        style={{ opacity }}
        className="absolute bottom-6 right-5 md:right-8 z-10 flex items-center gap-2 text-steel-400 text-xs tracking-[0.2em] uppercase"
      >
        {t.hero.scroll}
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.6 }}>
          <ArrowDown className="h-4 w-4" />
        </motion.span>
      </motion.div>
    </section>
  );
}
